import { useCallback, useEffect, useRef, useState } from 'react';
import { parse } from 'graphql';

import { createAuthenticatedGraphQLClient } from '../auth/createAuthenticatedGraphQLClient';
import { resolveClientEnv } from '../graphql/config/resolveClientEnv';
import type {
  DoorVisitDetailQuery,
  DoorVisitDetailQueryVariables,
} from '../graphql/queries/doorVisitDetail.generated';

// ─── Public Types ─────────────────────────────────────────────────────────────

// null means the server returned no record (not-found or forbidden — treated
// identically from the client's perspective to avoid exposing account isolation
// details through a distinct 403 state).
export type DoorVisitDetail = NonNullable<DoorVisitDetailQuery['doorVisitDetail']>;

type FetchImpl = Parameters<typeof createAuthenticatedGraphQLClient>[0]['fetchImpl'];

export type UseDoorVisitDetailOptions = {
  doorVisitId: number;
  fetchImpl?: FetchImpl;
};

export type UseDoorVisitDetailResult = {
  data: DoorVisitDetail | null;
  loading: boolean;
  error: string | null;
  backgroundLoading: boolean;
  backgroundError: string | null;
  refetch: () => void;
};

type BackgroundRefreshResult =
  | { kind: 'success'; data: DoorVisitDetail | null }
  | { kind: 'failed' | 'aborted' | 'in-flight' };

const BACKGROUND_REFRESH_INTERVAL_MS = 5_000;
const BACKGROUND_REFRESH_TIMEOUT_MS = 5 * 60_000;

const isEligibleForInsightPolling = (visit: DoorVisitDetail, routeDoorVisitId: number): boolean =>
  visit.doorVisitId === routeDoorVisitId
  && visit.hasSummary === false
  && (visit.reprocessedFromDoorVisitId !== null || visit.replayCount > 0);

// ─── GraphQL Document ─────────────────────────────────────────────────────────

export const DOOR_VISIT_DETAIL_DOCUMENT = parse(/* GraphQL */ `
  query DoorVisitDetail($doorVisitId: Int!) {
    doorVisitDetail(doorVisitId: $doorVisitId) {
      doorVisitId
      sessionId
      dwellStartedAt
      dwellEndedAt
      canvasserId
      address
      foHouseholdId
      matchedHouseholdId
      lat
      lng
      visitStatus
      isPartial
      coreLeadId
      coreCallId
      hasClip
      hasSummary
      reprocessedFromDoorVisitId
      replayCount
      voterName
      coreInsights {
        conversationSummary
        hasRecording
        candidateSentiments {
          candidateName
          sentiment
        }
        observations
        conversationalTags
        customSlots {
          key
          label
          value
          valueLabel
        }
      }
    }
  }
`);

// ─── Hook ─────────────────────────────────────────────────────────────────────

export const useDoorVisitDetail = ({
  doorVisitId,
  fetchImpl,
}: UseDoorVisitDetailOptions): UseDoorVisitDetailResult => {
  const [data, setData] = useState<DoorVisitDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [backgroundLoading, setBackgroundLoading] = useState(false);
  const [backgroundError, setBackgroundError] = useState<string | null>(null);
  const [automaticPollingEnabled, setAutomaticPollingEnabled] = useState(true);
  const [initialRequestAttempt, setInitialRequestAttempt] = useState(0);
  const dataRef = useRef<DoorVisitDetail | null>(null);
  const backgroundRequestInFlightRef = useRef(false);
  const backgroundAbortControllerRef = useRef<AbortController | null>(null);
  const detailRouteStartedAtRef = useRef({ doorVisitId, startedAt: Date.now() });

  if (detailRouteStartedAtRef.current.doorVisitId !== doorVisitId) {
    detailRouteStartedAtRef.current = { doorVisitId, startedAt: Date.now() };
  }

  dataRef.current = data;

  const fetchDoorVisitDetail = useCallback(
    async (signal: AbortSignal): Promise<DoorVisitDetail | null> => {
      const env = resolveClientEnv();
      const client = createAuthenticatedGraphQLClient({ env, fetchImpl });
      const variables: DoorVisitDetailQueryVariables = { doorVisitId };
      const response = await client.request<DoorVisitDetailQuery>({
        document: DOOR_VISIT_DETAIL_DOCUMENT,
        variables,
        signal,
      });

      // doorVisitDetail is null when the record does not exist or belongs to
      // another account — the not-found and forbidden cases are intentionally
      // collapsed here so the UI cannot distinguish them.
      return response.doorVisitDetail ?? null;
    },
    [doorVisitId, fetchImpl],
  );

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    const run = async (): Promise<void> => {
      setLoading(true);
      setError(null);
      setBackgroundError(null);
      setBackgroundLoading(false);
      setAutomaticPollingEnabled(true);

      try {
        const response = await fetchDoorVisitDetail(controller.signal);
        if (!cancelled) {
          setData(response);
        }
      } catch (err) {
        if (!cancelled && !controller.signal.aborted) {
          setError(err instanceof Error ? err.message : 'Failed to load door visit.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void run();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [fetchDoorVisitDetail, initialRequestAttempt]);

  const abortBackgroundRefresh = useCallback((): void => {
    const controller = backgroundAbortControllerRef.current;
    if (controller === null) {
      return;
    }

    // Clear ownership before aborting so a request that does not promptly reject
    // after cancellation cannot block a later manual refresh or a new route.
    backgroundAbortControllerRef.current = null;
    backgroundRequestInFlightRef.current = false;
    controller.abort();
  }, []);

  const refreshInBackground = useCallback(async (): Promise<BackgroundRefreshResult> => {
    if (backgroundRequestInFlightRef.current) {
      return { kind: 'in-flight' };
    }

    const controller = new AbortController();
    backgroundRequestInFlightRef.current = true;
    backgroundAbortControllerRef.current = controller;
    setBackgroundLoading(true);

    try {
      const nextData = await fetchDoorVisitDetail(controller.signal);
      if (!controller.signal.aborted) {
        setData(nextData);
        setBackgroundError(null);
        return { kind: 'success', data: nextData };
      }

      return { kind: 'aborted' };
    } catch (err) {
      if (!controller.signal.aborted) {
        setBackgroundError(err instanceof Error ? err.message : 'Failed to refresh door visit.');
        setAutomaticPollingEnabled(false);
        return { kind: 'failed' };
      }

      return { kind: 'aborted' };
    } finally {
      if (backgroundAbortControllerRef.current === controller) {
        backgroundAbortControllerRef.current = null;
        backgroundRequestInFlightRef.current = false;
        if (!controller.signal.aborted) {
          setBackgroundLoading(false);
        }
      }
    }
  }, [fetchDoorVisitDetail]);

  const shouldPoll = automaticPollingEnabled
    && data !== null
    && isEligibleForInsightPolling(data, doorVisitId);

  useEffect(() => {
    if (!shouldPoll) {
      return undefined;
    }

    let active = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const deadline = detailRouteStartedAtRef.current.startedAt + BACKGROUND_REFRESH_TIMEOUT_MS;

    const stopAtDeadline = (): void => {
      if (!active) {
        return;
      }

      active = false;
      if (timer !== undefined) {
        clearTimeout(timer);
      }
      abortBackgroundRefresh();
      setBackgroundLoading(false);
      setBackgroundError('Background refresh timed out.');
      setAutomaticPollingEnabled(false);
    };

    const remainingMs = deadline - Date.now();
    if (remainingMs <= 0) {
      stopAtDeadline();
      return undefined;
    }

    const deadlineTimer = setTimeout(stopAtDeadline, remainingMs);

    const scheduleNextRefresh = (): void => {
      const refreshRemainingMs = deadline - Date.now();
      if (!active || refreshRemainingMs <= 0) {
        return;
      }

      timer = setTimeout(() => {
        void (async () => {
          if (!active || Date.now() >= deadline) {
            return;
          }

          const result = await refreshInBackground();
          if (
            !active
            || result.kind !== 'success'
            || result.data === null
            || !isEligibleForInsightPolling(result.data, doorVisitId)
          ) {
            return;
          }

          scheduleNextRefresh();
        })();
      }, Math.min(BACKGROUND_REFRESH_INTERVAL_MS, refreshRemainingMs));
    };

    scheduleNextRefresh();

    return () => {
      active = false;
      if (timer !== undefined) {
        clearTimeout(timer);
      }
      clearTimeout(deadlineTimer);
      abortBackgroundRefresh();
    };
  }, [abortBackgroundRefresh, doorVisitId, refreshInBackground, shouldPoll]);

  useEffect(
    () => () => {
      abortBackgroundRefresh();
    },
    [abortBackgroundRefresh, doorVisitId],
  );

  const refetch = useCallback((): void => {
    if (dataRef.current === null || dataRef.current.doorVisitId !== doorVisitId) {
      setInitialRequestAttempt(current => current + 1);
      return;
    }

    void (async () => {
      const result = await refreshInBackground();
      const deadline = detailRouteStartedAtRef.current.startedAt + BACKGROUND_REFRESH_TIMEOUT_MS;
      if (result.kind === 'success' && Date.now() < deadline) {
        setAutomaticPollingEnabled(true);
      }
    })();
  }, [doorVisitId, refreshInBackground]);

  return { data, loading, error, backgroundLoading, backgroundError, refetch };
};
